import { useMutation } from "@tanstack/react-query";
import { apiAxios, ifsApi } from "lib/axios";

// ─── Types ───

export interface GeoPoint {
  lat: number;
  lng: number;
}

export interface LocationInfo {
  location: string;
  lat: number;
  lng: number;
}

export interface FuelLocation {
  /** Amount of fuel to purchase in gallons (-99 means "top off") */
  fuelToPurchase: number;
  lat: number;
  lng: number;
  /** Name and address of the fuel location */
  location: string;
  /** Fuel price per gallon */
  price: number;
  /** Whether this location is included (1) or not (0) */
  include: number;
  interstate_exit: string;
}

export interface TollLocation {
  name: string;
  latitude: number;
  longitude: number;
  tollSystem: string;
  price: number;
}

export interface FuelStationOnRoute {
  /** Tuples of [location, price, lat, lng, interstate_exit] */
  data: [string, number, number, number, string][];
}

export interface FuelPlanRoute {
  routeNumber: number;
  origin: LocationInfo;
  destination: LocationInfo;
  directionsVia: unknown[];
  totalTripDistanceMiles: number;
  totalFuelNeededGallons: number;
  totalPurchaseNeededGallons: number;
  ifsOptimizedSavings: number;
  fuelPurchaseLocations: FuelLocation[];
  fuelStationOnRoute: FuelStationOnRoute;
  decodedPolyline: GeoPoint[];
  /** Trip duration in hours */
  duration: number;
  totalGallonsPurchased: number;
  tollLocations: TollLocation[];
  optimalRanking: number;
  /** Human-readable route label (Pro), e.g. "Fastest" | "Balanced" | "Cheapest" */
  routeType?: string;
  totalCost: number;
  preIftaFuelPM: number;
  preIftafuelcost: number;
  iftaTaxPerMile: number;
  optimalPrice: number;
  tollCost: number;
  totalMiles: number;
  totalGallonsBurned: number;
  averageMPH: number;
  /** Pro-only turn summary */
  brief_directions?: string[];
  /** Present when the backend returns a 200 with an error payload */
  error?: string;
}

export interface FuelPlanProParams {
  customer: string;
  origin: string;
  destination: string;
  via: string[];
  /** Current fuel level in gallons */
  tractorFuel: number;
  /** Fuel tank capacity in gallons */
  tractorFuelCapacity: number;
  MPG: number;
  routeMode: "short" | "fast";
}

export interface FuelPlanRecordVariables {
  customer: string;
  unitNumber: string;
  fuelPlan: Record<string, unknown>;
}

// ─── Mutations ───
// Fuel plan generation is triggered by form submits (an action, not background
// state), so this whole domain is modeled as mutations.

/**
 * Gets the fuel plan with optimization details (Pro). Returns one route per
 * ranking, or an `{ error }` payload on a 200 response.
 */
async function getFuelPlanPro({
  customer,
  origin,
  destination,
  via,
  tractorFuel,
  tractorFuelCapacity,
  MPG,
  routeMode,
}: FuelPlanProParams): Promise<FuelPlanRoute[] | { error: string }> {
  const response = await apiAxios.get("/fuel_solution_generate_pro", {
    params: {
      cust: customer,
      orig: origin,
      dest: destination,
      via: JSON.stringify(via),
      tanklvl: tractorFuel,
      fuel_capacity: tractorFuelCapacity,
      MPG,
      route_mode: routeMode,
    },
  });
  return response.data;
}

async function storeFuelPlan(
  customer: string,
  unitNumber: string,
  fuelPlan: Record<string, unknown>
): Promise<void> {
  await apiAxios.post("/fuel-plans", { customer, unitNumber, ...fuelPlan });
}

async function queueFuelPlan(
  customer: string,
  unitNumber: string,
  fuelPlan: Record<string, unknown>
): Promise<void> {
  await ifsApi.post(`/queue-fuel-plan`, { customer, unitNumber, ...fuelPlan });
}

export function useGenerateFuelPlanPro() {
  return useMutation({ mutationFn: getFuelPlanPro });
}

export function useStoreFuelPlan() {
  return useMutation({
    mutationFn: ({ customer, unitNumber, fuelPlan }: FuelPlanRecordVariables) =>
      storeFuelPlan(customer, unitNumber, fuelPlan),
  });
}

export function useQueueFuelPlan() {
  return useMutation({
    mutationFn: ({ customer, unitNumber, fuelPlan }: FuelPlanRecordVariables) =>
      queueFuelPlan(customer, unitNumber, fuelPlan),
  });
}
